import { Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCirclePlus,
  faFileCircleMinus,
  faClipboardCheck,
  faCalendarPlus,
  faCreditCard,
  faHourglassHalf,
  faFileContract,
  faDownload,
  faBoxOpen,
  faExchangeAlt,
  faBoxes,
  faFileExport,
  faFileImport,
  faCheckDouble,
  faLinkSlash,
  faClone,
  faUserCheck,
  faHandshake,
  faCodeMerge,
  faPlay,
  faArrowsRotate,
  faBoxArchive,
  faPaperPlane,
  faPause,
  faRefresh,
  faCalendarDays,
  faClockRotateLeft,
  faBan,
  faHandHoldingHeart,
  faCircleXmark,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import {
  DeleteIcon,
  EditIcon,
  EyeIcon,
  PrintIcon,
  RotateLeftIcon,
  StopServiceIcon,
} from "./svg-action-icons";
import { TerminateIcon } from "./svg-action-icons/Terminate";
import { AssignServiceIcon } from "./svg-action-icons/AssignService";
import { PayDuesIcon } from "./svg-action-icons/PayDues";
import {
  CAUTION_ACTION_TYPES,
  DESTRUCTIVE_ACTION_TYPES,
  LOGOUT_ALL_DEVICES_ACTION_TYPE,
} from "constants/general";

const { confirm } = Modal;

/*
 * `rgb(...)` around the token, not the bare `var()`. `--color-danger` is a channel
 * triple (`243 39 39`), so `color: var(--color-danger)` is not a colour at all — it
 * was dropped as invalid and the swatch inherited black. Only the `*-bg-soft`
 * tokens worked, because those are already whole `rgba()` values, which is why a
 * destructive action has read as a pink square with a blue icon.
 */
const ACTION_STYLES = {
  destructive: {
    color: "rgb(var(--color-danger))",
    backgroundColor: "var(--color-danger-bg-soft)",
  },
  caution: {
    color: "rgb(var(--color-warning))",
    backgroundColor: "var(--color-warning-bg-soft)",
  },
  default: {
    color: "rgb(var(--color-primary))",
    backgroundColor: "var(--color-primary-bg-soft)",
  },
};

export const getActionTone = (type) => {
  if (DESTRUCTIVE_ACTION_TYPES.includes(type)) return "destructive";
  if (CAUTION_ACTION_TYPES.includes(type)) return "caution";
  return "default";
};

export const getActionButtonStyle = (type) =>
  ACTION_STYLES[getActionTone(type)];

export const Icon = ({ type, color }) => {
  const iconMap = {
    Delete: <DeleteIcon color={color} />,
    Update: <EditIcon color={color} />,
    View: <EyeIcon color={color} />,
    Print: <PrintIcon color={color} />,
    AddService: (
      <AssignServiceIcon color={color || "rgb(var(--color-brand))"} />
    ),
    AddCreditNote: <FontAwesomeIcon icon={faFileCirclePlus} />,
    AddDebitNote: <FontAwesomeIcon icon={faFileCircleMinus} />,
    AddSalesRefund: <RotateLeftIcon color={color} />,
    AddPurchaseRefund: <RotateLeftIcon color={color} />,
    PayDues: <PayDuesIcon color={color || "rgb(var(--color-brand))"} />,
    Complete: <FontAwesomeIcon icon={faClipboardCheck} />,
    FullFilled: <FontAwesomeIcon icon={faCheckDouble} />,
    AddPayment: <FontAwesomeIcon icon={faCalendarPlus} />,
    AddPaymentMethod: <FontAwesomeIcon icon={faCreditCard} />,
    Pending: <FontAwesomeIcon icon={faHourglassHalf} />,
    ViewContract: <FontAwesomeIcon icon={faFileContract} />,
    Download: <FontAwesomeIcon icon={faDownload} />,
    Receive: <FontAwesomeIcon icon={faBoxOpen} />,
    Transfer: <FontAwesomeIcon icon={faExchangeAlt} />,
    Supply: <FontAwesomeIcon icon={faBoxes} />,
    Export: <FontAwesomeIcon icon={faFileExport} />,
    Import: <FontAwesomeIcon icon={faFileImport} />,
    Terminate: <TerminateIcon color={color || "#FF383C"} />,
    StopService: <StopServiceIcon />,
    // Refusing someone else's request, which is not the same act as withdrawing
    // your own — and a row offering both (a leave request, a payroll run) drew
    // one icon twice, so the two were told apart only by a tooltip. It also gave
    // `ActionIconsButton` two children keyed on the same `type`.
    Reject: <FontAwesomeIcon icon={faCircleXmark} />,
    // The counterpart to StopService, and deliberately not in
    // DESTRUCTIVE_ACTION_TYPES: stopping a service contract is the red action,
    // starting it again is not. Previously this reused `AddService`, which drew
    // the assign-a-service-to-a-unit icon — a different operation entirely.
    ResumeService: <FontAwesomeIcon icon={faPlay} />,
    // Renew is not Resume. `ResumeService`'s play triangle reads as "start this
    // again"; renewing a document issues a new term and a new version of it, which
    // is the rotate metaphor.
    Renew: <FontAwesomeIcon icon={faArrowsRotate} />,
    // Taking a record off a register and putting it back. Deliberately not
    // StopService/FullFilled, which the document module borrowed: stopping reads as
    // halting something running, and the double-check as "completed" — archiving is
    // neither, and it is reversible, so it is not in DESTRUCTIVE_ACTION_TYPES.
    Archive: <FontAwesomeIcon icon={faBoxArchive} />,
    Unarchive: <FontAwesomeIcon icon={faBoxOpen} />,
    UnlinkEjar: <FontAwesomeIcon icon={faLinkSlash} />,
    Restore: <RotateLeftIcon color={color} />,
    Clone: <FontAwesomeIcon icon={faClone} />,
    // CRM: move a lead through qualification, and turn a qualified one into a deal.
    Qualify: <FontAwesomeIcon icon={faUserCheck} />,
    Convert: <FontAwesomeIcon icon={faHandshake} />,
    Merge: <FontAwesomeIcon icon={faCodeMerge} />,
    // Deliver a letter to its recipient over the notification channels.
    Send: <FontAwesomeIcon icon={faPaperPlane} />,
    Pause: <FontAwesomeIcon icon={faPause} />,
    Schedule: <FontAwesomeIcon icon={faCalendarDays} />,
    Reschedule: <FontAwesomeIcon icon={faClockRotateLeft} />,
    Cancel: <FontAwesomeIcon icon={faBan} />,
    Republish: <FontAwesomeIcon icon={faRefresh} />,
    // Forgiving what someone still owes. Amber, not red: nothing is deleted, the
    // office takes the cost on itself.
    Waive: <FontAwesomeIcon icon={faHandHoldingHeart} />,
    [LOGOUT_ALL_DEVICES_ACTION_TYPE]: (
      <FontAwesomeIcon icon={faRightFromBracket} />
    ),
  };

  return iconMap[type] || null;
};

export const handleActionClick = (action, type, t, confirmTitle) => {
  if (typeof action !== "function") return;

  const onOk = () => action();

  if (type === "Delete") {
    confirm({
      title: confirmTitle || t("delete_popup"),
      onOk,
      onCancel() {},
      okText: t("okTitle"),
      cancelText: t("cancelTitle"),
    });
  } else if (type === "Terminate") {
    confirm({
      title: t("terminate_contract_confirmation"),
      onOk,
      onCancel() {},
      okText: t("okTitle"),
      cancelText: t("cancelTitle"),
    });
  } else if (type === "UnlinkEjar") {
    confirm({
      title: t("ejar.actions.unlinkConfirm"),
      onOk,
      onCancel() {},
      okText: t("okTitle"),
      cancelText: t("cancelTitle"),
    });
  } else if (type === "Restore") {
    confirm({
      title: t("softDelete.restoreConfirm"),
      onOk,
      onCancel() {},
      okText: t("okTitle"),
      cancelText: t("cancelTitle"),
    });
  } else if (type === LOGOUT_ALL_DEVICES_ACTION_TYPE) {
    confirm({
      title: confirmTitle || t("logoutAllDevices.confirm"),
      onOk,
      onCancel() {},
      okText: t("okTitle"),
      cancelText: t("cancelTitle"),
    });
  } else if (type === "Clone") {
    confirm({
      title: t("clone.confirm_title"),
      onOk,
      onCancel() {},
      okText: t("clone.confirm_ok"),
      cancelText: t("clone.confirm_cancel"),
    });
  } else {
    action();
  }
};
